import { lazy, Suspense, useEffect, useState } from "react";

// Lazy-load the WebGL/Three.js backdrop so mobile never downloads or executes
// three.js during the critical render path. This was the main cause of the
// Lighthouse NO_LCP / 4.7s FCP report on mobile.
const WebGLBackdrop = lazy(() => import("./three/WebGLBackdrop"));

const Hero = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsDesktop(
        window.innerWidth >= 1024 &&
          !window.matchMedia("(pointer: coarse)").matches
      );
    check();
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);

  const stats = [
    { value: "50+", label: "Revenue Projects Shipped" },
    { value: "12+", label: "Industries Served" },
    { value: "15+", label: "Countries Reached" },
    { value: "98%", label: "Client Retention" }
  ];

  const floatingChips = [
    { symbol: "↗", label: "Digital Marketing & Paid Ads", x: "3%", y: "16%", delay: 0.15, accent: "32 95% 60%" },
    { symbol: "{}", label: "Web & App Development", x: "84%", y: "14%", delay: 0.25, accent: "270 95% 68%" },
    { symbol: "✦", label: "Graphic Design & UI/UX", x: "2%", y: "44%", delay: 0.35, accent: "330 95% 68%" },
    { symbol: "▣", label: "Shopify Stores", x: "85%", y: "42%", delay: 0.45, accent: "200 95% 68%" },
    { symbol: "AI", label: "AI Automation", x: "5%", y: "72%", delay: 0.55, accent: "285 95% 70%" },
    { symbol: "POS", label: "Custom POS Software", x: "83%", y: "74%", delay: 0.65, accent: "180 95% 60%" }
  ];

  return (
    <section
      id="home"
      data-accent="blue-amber"
      className="relative min-h-screen overflow-hidden bg-[#0a0d18]"
    >
      {/* Live WebGL backdrop — desktop only, lazy-loaded after first paint */}
      {isDesktop && (
        <Suspense fallback={null}>
          <WebGLBackdrop accentA="270 95% 68%" accentB="32 95% 60%" variant="crystal" />
        </Suspense>
      )}

      {/* Vignette + scanline texture for cinematic feel */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-b from-transparent via-transparent to-background/95" />
      <div
        className="absolute inset-0 z-[1] pointer-events-none opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, hsla(0,0%,100%,0.4) 0 1px, transparent 1px 3px)",
        }}
      />

      {/* Aurora light beams */}
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[120vw] h-[60vh] z-[1] pointer-events-none opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, hsla(270, 95%, 60%, 0.32), transparent 55%), radial-gradient(ellipse at 70% 60%, hsla(32, 95%, 55%, 0.22), transparent 55%)",
        }}
        aria-hidden
      />

      {/* Floating service chips — desktop only, 3D depth */}
      <div className="hidden lg:block absolute inset-0 z-[2] pointer-events-none">
        {floatingChips.map((c, i) => (
          <div
            key={i}
            className="absolute animate-fade-in"
            style={{ left: c.x, top: c.y, animationDelay: `${c.delay}s`, animationFillMode: "both" }}
          >
            <div
              className="flex items-center gap-2 rounded-full px-4 py-2.5 backdrop-blur-2xl border border-cream/10 animate-float"
              style={{
                background: `linear-gradient(135deg, hsla(${c.accent}, 0.18), hsla(224, 35%, 8%, 0.6))`,
                boxShadow: `0 8px 32px hsla(${c.accent}, 0.28), inset 0 1px 0 hsla(40, 35%, 94%, 0.08)`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              <span
                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{ background: `hsla(${c.accent}, 0.25)`, boxShadow: `0 0 18px hsla(${c.accent}, 0.5)` }}
              >
                <span style={{ color: `hsl(${c.accent})` }}>{c.symbol}</span>
              </span>
              <span className="text-xs font-medium text-cream/90 whitespace-nowrap">{c.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24 min-h-screen flex flex-col">
        {/* Text Content */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="flex items-center gap-2 bg-cream/[0.04] backdrop-blur-2xl border border-cream/10 rounded-full px-5 py-2.5 shadow-[0_0_40px_hsla(270,95%,60%,0.25)]">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse shadow-[0_0_12px_hsl(var(--primary))]" />
              <span className="text-cream/85 text-sm font-medium tracking-wide">Digital Marketing • Web &amp; App • Shopify • POS • AI • Graphic Design &amp; UI/UX</span>
            </div>
          </div>

          {/* Headline — rendered statically (no opacity:0 initial) so it is
              the immediate LCP candidate. Fixes mobile NO_LCP. */}
          <div className="mb-8">
            <h1 className="sr-only">
              Digital Marketing, Graphic Design &amp; UI/UX, Shopify, POS Software and Web &amp; Mobile App Development Agency — Zaibify Digital Solutions
            </h1>
            <p
              aria-hidden
              className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-cream/85 leading-[1.15] tracking-tight"
            >
              Digital Marketing, Graphic Design, Shopify, POS &amp; App Development — Engineered Under One Roof
            </p>
            <p className="text-5xl md:text-7xl lg:text-[7.5rem] font-serif font-bold leading-[0.95] mt-6 tracking-[-0.03em]">
              <span className="text-gradient-glow block">Build.</span>
              <span className="text-cream/95 block">Launch.</span>
              <span className="text-gradient-glow block">Scale.</span>
            </p>
          </div>

          {/* Bio — static so it contributes to first paint */}
          <p className="text-cream/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
            We engineer high-converting digital marketing campaigns, custom websites, Shopify stores, mobile apps, AI automation, industry-specific POS software and brand-defining graphic design &amp; UI/UX — for ambitious brands across the UK, USA, Europe and the Gulf, built to rank on Google, convert visitors and scale revenue from day one.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <a
              href="#projects"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-semibold text-base hover-lift"
              style={{ boxShadow: '0 4px 28px hsla(270, 95%, 60%, 0.45)' }}
            >
              <span>See Case Studies</span>
              <span className="text-xl leading-none group-hover:translate-x-1 transition-transform duration-200">→</span>
            </a>
            <a
              href="#contact"
              className="group inline-flex items-center gap-3 border border-cream/15 text-cream px-8 py-4 rounded-full font-semibold text-base backdrop-blur-sm hover-lift hover:border-cream/30 hover:bg-cream/5"
            >
              <span>Book a Strategy Call</span>
              <div className="w-9 h-9 rounded-full bg-cream/8 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-200">
                <span className="text-xs leading-none">▶</span>
              </div>
            </a>
          </div>

          {/* Customer Rating */}
          <div className="flex items-center justify-center gap-5">
            <div className="flex -space-x-3">
              {[
                { initials: "AK", gradient: "linear-gradient(135deg, hsl(270, 80%, 55%), hsl(285, 80%, 60%))" },
                { initials: "SM", gradient: "linear-gradient(135deg, hsl(32, 90%, 55%), hsl(15, 85%, 55%))" },
                { initials: "JR", gradient: "linear-gradient(135deg, hsl(200, 80%, 55%), hsl(220, 80%, 55%))" },
                { initials: "LH", gradient: "linear-gradient(135deg, hsl(330, 80%, 60%), hsl(290, 80%, 60%))" },
              ].map((c, i) => (
                <div
                  key={i}
                  role="img"
                  aria-label={`Satisfied client ${i + 1}`}
                  className="w-11 h-11 rounded-full border-2 border-background flex items-center justify-center text-cream text-[11px] font-semibold tracking-wide"
                  style={{ background: c.gradient }}
                >
                  {c.initials}
                </div>
              ))}
              <div className="w-11 h-11 rounded-full bg-primary border-2 border-background flex items-center justify-center">
                <span className="text-xs font-bold text-primary-foreground">50+</span>
              </div>
            </div>
            <div>
              <p className="text-cream text-sm font-medium">Rated 5 Stars by 50+ Clients</p>
              <p className="text-cream/72 text-xs">Across the Globe</p>
              <div className="flex gap-0.5 mt-1.5 justify-center">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-primary text-sm">★</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-3xl mx-auto mt-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-2xl md:text-4xl font-serif font-bold text-gradient-glow mb-1">{stat.value}</p>
              <p className="text-cream/72 text-xs md:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="flex flex-col items-center gap-2 text-cream/62">
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <div className="w-5 h-8 border border-cream/20 rounded-full flex justify-center pt-1.5">
              <div className="w-1 h-2 bg-cream/40 rounded-full animate-bounce" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
